import type { ColDef } from 'ag-grid-community'
import { upperFirst } from 'lodash-es'
import {
  applyTableGroupHeaderStyleToColDefs,
  createScientificNotationValueFormatter,
  generateComparsionTableStructure,
  mapColumnDefinitionsToColDefs
} from '@jana-technology/lht-qra-table/dist/utils'

import { ITableObject, QRADatasetRowEntry } from '$/types'

import { IGroupColumnsSelection } from 'features/RiskManagement/Components/Tables'

import {
  applyFloatValueFormatter,
  lengthFormatter,
  nullValueFormatter
} from './formatters'
import { applyGroupColumnsSettingToColDefs } from './groupColumns'

// #region types
type Nullish = null | undefined | ''
type QRADatasetRowEntryKey<Y> = keyof QRADatasetRowEntry<Y>
type QRARowDataModified<Y> = {
  [Key in QRADatasetRowEntryKey<Y>]: QRADatasetRowEntry<Y>[Key] extends boolean
    ? Capitalize<string>
    : QRADatasetRowEntry<Y>[Key] extends Nullish
      ? 'NULL'
      : QRADatasetRowEntry<Y>[Key]
}
// #endregion

/**
 * Temp workaround to handle boolean QRA table coldef
 * convert boolean type to string to prevent table from using
 * the checkbox formatter
 *
 * NOTE: After switching away from QRA table view, we can
 *       look into overriding the boolean data type formatter
 */
const formatRowData = <Y>(rowDataIn: QRADatasetRowEntry<Y>) => {
  const rowDataOut = { ...rowDataIn }

  const entries = Object.entries(rowDataOut) as Array<
    [QRADatasetRowEntryKey<Y>, QRADatasetRowEntry<Y>[QRADatasetRowEntryKey<Y>]]
  >

  for (const [key, val] of entries) {
    if (val === null || val === undefined || val === '') {
      ;(rowDataOut[key] as string) = 'NULL'
    }

    if (typeof val === 'boolean') {
      ;(rowDataOut[key] as string) = upperFirst(String(val))
    }
  }

  return rowDataOut as QRARowDataModified<Y>
}

const extractTableProperties = <Y>(
  tableData?: ITableObject<Y>
): [
  Array<Partial<ColDef>>,
  QRARowDataModified<Y>[],
  QRARowDataModified<Y>[],
  string,
  string
] => {
  // REVIEW: this will be a reduced set of ColDefs from our API
  // NOTE: API does not support grouped ColDefs at the momenet
  const colDefs: Array<Partial<ColDef>> = []
  const set1RowData: QRARowDataModified<Y>[] = []
  const set2RowData: QRARowDataModified<Y>[] = []
  const state1Name = tableData?.dataset1Rows?.name ?? 'State 1'
  const state2Name = tableData?.dataset2Rows?.name ?? 'State 2'

  colDefs.splice(0, 0, ...(tableData?.columnDefinitions ?? []))

  tableData?.dataset1Rows.rows.forEach((tr) => {
    set1RowData.push(formatRowData<Y>(tr.rowEntry))
  })

  tableData?.dataset2Rows?.rows.forEach((tr) => {
    set2RowData.push(formatRowData<Y>(tr.rowEntry))
  })

  return [colDefs, set1RowData, set2RowData, state1Name, state2Name]
}

/**
 * Converts response from table API to props expected by QRATable component
 * @param tableData from API response
 * @returns column definition and row data props for QRATable component
 */
export const convertTableAPIDataToQRATableComponentProps = <Y>(
  tableData: ITableObject<Y> | undefined,
  groupColumns: IGroupColumnsSelection[],
  isCompareTable = false
): [ColDef[], QRARowDataModified<Y>[]] => {
  const [colDefsBase, dataset1, dataset2, state1Name, state2Name] =
    extractTableProperties<Y>(tableData)

  const colDefsWithFormattedTitle = colDefsBase.map((cd) => {
    const newCd = { ...cd }

    if (cd.headerName != null) {
      /**
       * NOTE: QRA input table contains URL encoded string to represent
       *       unit type.
       *
       * This should not be necessary after switch to new ColDefs API as
       * it should contain a unitType field which we can handle similar
       * to table in Data Management.
       */
      newCd.headerName = decodeURIComponent(cd.headerName)
    }

    return newCd
  })

  let colDefs = applyGroupColumnsSettingToColDefs(
    colDefsWithFormattedTitle,
    groupColumns,
    0
  )

  let rowData = dataset1

  const additionalNoCompareColumnNames = [
    'Name',
    'ContinuousRouteId',
    'SegmentId',
    'RouteName',
    // Output
    'Length',
    'LengthMiles',
    'ConsequenceAreaCode',
    'OHCAFlag',
    'ClassLocationCode',
    'FromMeasure',
    'ToMeasure'
    // Input
    // 'StartMeasure',
    // 'EndMeasure'
  ]

  // REVIEW: specify what kind of formatting? like decimal places?
  const fieldsForSpecialFormatting = [
    'WallThickness',
    'BlastingSusceptibility',
    'CarbonDioxideMoleFraction',
    'CharpyVNotchToughness',
    'CpSystemPerformance',
    'DependentCustomerCount',
    'DepthOfCover',
    'DepthOfFrostLine',
    'EarthMvmntSusceptibility',
    'FloodSusceptibility',
    'WeldCoatingExtent',
    'HydrogenSulfideMoleFraction',
    'IsolationSectionVolume',
    'LightningStrikeDensity',
    'OneCallResponseTime',
    'OperatingPressure',
    'OperatingTemperature',
    'OutsideDiameter',
    'OxygenMoleFraction',
    'PatrolInterval',
    'SoilResistivity',
    'Ssi',
    'SubsidenceSusceptibility',
    'TensileStrength',
    'WaterMoleFraction',
    'YieldStrength',
    'YieldStrengthPsi'
  ]

  const fixedDecimalColumnNames = [
    'FromMeasure',
    'ToMeasure',
    'WallThickness',
    'IsolationSectionVolume'
  ]

  // TODO: null value formatter should be applied to all integer columns
  const nullSafeColumnNames = [
    'ExternalCorrosionRate',
    'InternalCorrosionRate',
    'ReassessmentInterval'
  ]

  const lengthColumnNames = ['Length', 'LengthMiles']

  const applyScientificNotation = createScientificNotationValueFormatter(
    2,
    additionalNoCompareColumnNames.concat(fieldsForSpecialFormatting),
    true
  )

  if (isCompareTable) {
    // Assume identifier is the first column in layout
    const idColumnName = colDefs[0]?.field

    const { colDefs: compareColDefs, rowData: compareRowData } =
      generateComparsionTableStructure(
        mapColumnDefinitionsToColDefs(colDefs),
        dataset1,
        dataset2,
        state1Name,
        state2Name,
        additionalNoCompareColumnNames,
        idColumnName
      )

    colDefs = applyScientificNotation(
      applyTableGroupHeaderStyleToColDefs(compareColDefs)
    )
    rowData = compareRowData
  } else {
    // ! this mutation changes headerName fields to route name
    // ! for now, applyGroupColumnsSettingToColDefs is handling formatting
    // colDefs = applyscientificNotation(
    //   applyTableGroupHeaderStyleToColDefs(colDefs)
    // )
    colDefs = applyScientificNotation(colDefsWithFormattedTitle)
  }

  const colDefsByField = colDefs.reduce(
    (acc, colDef) => {
      if (colDef.field) {
        acc[colDef.field] = colDef
      }

      return acc
    },
    {} as Record<string, ColDef>
  )

  fixedDecimalColumnNames.forEach((field) => {
    const fieldColDef = colDefsByField[field]

    if (fieldColDef) {
      applyFloatValueFormatter(fieldColDef)
    }
  })

  nullSafeColumnNames.forEach((field) => {
    const fieldColDef = colDefsByField[field]

    if (fieldColDef) {
      fieldColDef.valueFormatter = nullValueFormatter
    }
  })

  lengthColumnNames.forEach((field) => {
    const fieldColDef = colDefsByField[field]

    if (fieldColDef) {
      fieldColDef.valueFormatter = lengthFormatter
    }
  })

  return [colDefs, rowData]
}

// #region quickFilter helpers
// based on initial requirements in LHT-2739
export const quickFilterParser = (searchText: string) => {
  // split search terms by ; and remove empty strings
  const allParts = searchText
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)

  // return unique values only to quickFilterMatcher for a more search-like feel
  return Array.from(new Set(allParts))
}

// this function will be needed if we want to match formatted values
export const quickFilterMatcher = (
  quickFilterParts: string[],
  rowQuickFilterAggregateText: string
) => {
  // quickFilterParts are filtered by quickFilterParser

  // this is the default logic, so this function is not really needed yet
  return quickFilterParts.every((part) =>
    rowQuickFilterAggregateText.includes(part)
  )
}

// #endregion
